/**
 * File Transfer Panel — WC-8/9/10
 * Slide-in panel with remote file browser, upload via drag-and-drop, and transfer progress.
 */

import { FileTransferService, FileTransferProgress, RemoteFileEntry } from '../services/FileTransferService';

export interface FileTransferPanelConfig {
    fileTransferService: FileTransferService;
}

export class FileTransferPanel {
    private service: FileTransferService;
    private panel: HTMLElement | null = null;
    private isOpen = false;
    private currentRemotePath = 'C:\\';
    private remoteEntries: RemoteFileEntry[] = [];

    constructor(config: FileTransferPanelConfig) {
        this.service = config.fileTransferService;
    }

    /** Toggle panel open/close */
    toggle(): void {
        if (this.isOpen) {
            this.close();
        } else {
            this.open();
        }
    }

    /** Open the panel */
    async open(): Promise<void> {
        if (this.isOpen) return;
        this.isOpen = true;
        this.createPanel();
        // Trigger slide-in animation
        requestAnimationFrame(() => {
            this.panel?.classList.add('ftp-open');
        });
        await this.browseRemote(this.currentRemotePath);
    }

    /** Close the panel */
    close(): void {
        if (!this.isOpen) return;
        this.isOpen = false;
        const panelToRemove = this.panel;
        this.panel = null;
        panelToRemove?.classList.remove('ftp-open');
        setTimeout(() => {
            panelToRemove?.remove();
        }, 300); // Match CSS transition
    }

    get visible(): boolean {
        return this.isOpen;
    }

    private createPanel(): void {
        if (this.panel) this.panel.remove();

        this.panel = document.createElement('div');
        this.panel.className = 'ftp-panel';
        this.panel.innerHTML = `
            <div class="ftp-header">
                <span class="ftp-title">&#x1F4C1; העברת קבצים</span>
                <button class="ftp-close-btn" title="סגור">&times;</button>
            </div>
            <div class="ftp-body">
                <div class="ftp-remote-section">
                    <div class="ftp-path-bar">
                        <button class="ftp-up-btn" title="תיקיה למעלה">&#x2B06;</button>
                        <input class="ftp-path-input" type="text" value="${this.escapeHtml(this.currentRemotePath)}" dir="ltr" />
                        <button class="ftp-go-btn" title="עבור">&#x27A1;</button>
                    </div>
                    <div class="ftp-file-list" id="ftp-remote-list">
                        <div class="ftp-loading">טוען...</div>
                    </div>
                </div>
                <div class="ftp-drop-zone" id="ftp-drop-zone">
                    <div class="ftp-drop-text">&#x1F4E4; גרור קבצים לכאן להעלאה</div>
                    <input type="file" id="ftp-file-input" multiple style="display: none;" />
                    <button class="ftp-upload-btn" id="ftp-upload-btn">בחר קבצים...</button>
                </div>
                <div class="ftp-transfers" id="ftp-transfers"></div>
            </div>
        `;

        this.injectStyles();
        document.body.appendChild(this.panel);
        this.attachPanelEvents();
    }

    private attachPanelEvents(): void {
        if (!this.panel) return;

        // Close button
        this.panel.querySelector('.ftp-close-btn')?.addEventListener('click', () => this.close());

        // Path navigation
        const pathInput = this.panel.querySelector('.ftp-path-input') as HTMLInputElement;
        const goBtn = this.panel.querySelector('.ftp-go-btn');
        const upBtn = this.panel.querySelector('.ftp-up-btn');

        goBtn?.addEventListener('click', () => this.browseRemote(pathInput.value));
        pathInput?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') this.browseRemote(pathInput.value);
        });
        upBtn?.addEventListener('click', () => {
            const parent = this.currentRemotePath.replace(/\\[^\\]+\\?$/, '\\');
            if (parent !== this.currentRemotePath) {
                this.browseRemote(parent);
            }
        });

        // Drag-and-drop (WC-9)
        const dropZone = this.panel.querySelector('#ftp-drop-zone') as HTMLElement;
        dropZone?.addEventListener('dragover', (e) => {
            e.preventDefault();
            dropZone.classList.add('ftp-drop-active');
        });
        dropZone?.addEventListener('dragleave', () => {
            dropZone.classList.remove('ftp-drop-active');
        });
        dropZone?.addEventListener('drop', (e) => {
            e.preventDefault();
            dropZone.classList.remove('ftp-drop-active');
            if (e.dataTransfer?.files) {
                this.uploadFiles(Array.from(e.dataTransfer.files));
            }
        });

        // File input button
        const fileInput = this.panel.querySelector('#ftp-file-input') as HTMLInputElement;
        const uploadBtn = this.panel.querySelector('#ftp-upload-btn');
        uploadBtn?.addEventListener('click', () => fileInput?.click());
        fileInput?.addEventListener('change', () => {
            if (fileInput.files?.length) {
                this.uploadFiles(Array.from(fileInput.files));
                fileInput.value = '';
            }
        });
    }

    private async browseRemote(path: string): Promise<void> {
        const listEl = this.panel?.querySelector('#ftp-remote-list');
        if (!listEl) return;

        listEl.innerHTML = '<div class="ftp-loading">טוען...</div>';

        try {
            this.remoteEntries = await this.service.browseDirectory(path);
            this.currentRemotePath = path;

            const pathInput = this.panel?.querySelector('.ftp-path-input') as HTMLInputElement;
            if (pathInput) pathInput.value = path;

            this.renderFileList();
        } catch (error: any) {
            listEl.innerHTML = `<div class="ftp-error">שגיאה: ${this.escapeHtml(error.message || 'לא ניתן לגשת לתיקיה')}</div>`;
        }
    }

    private renderFileList(): void {
        const listEl = this.panel?.querySelector('#ftp-remote-list');
        if (!listEl) return;

        if (this.remoteEntries.length === 0) {
            listEl.innerHTML = '<div class="ftp-empty">תיקיה ריקה</div>';
            return;
        }

        // Sort: directories first, then files
        const sorted = [...this.remoteEntries].sort((a, b) => {
            if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
            return a.name.localeCompare(b.name);
        });

        listEl.innerHTML = sorted.map(entry => `
            <div class="ftp-entry ${entry.isDirectory ? 'ftp-dir' : 'ftp-file'}" data-path="${this.escapeHtml(entry.fullPath)}">
                <span class="ftp-entry-icon">${entry.isDirectory ? '&#x1F4C1;' : '&#x1F4C4;'}</span>
                <span class="ftp-entry-name">${this.escapeHtml(entry.name)}</span>
                <span class="ftp-entry-size">${entry.isDirectory ? '' : this.formatSize(entry.size)}</span>
                ${!entry.isDirectory ? '<button class="ftp-download-btn" title="הורד">&#x2B07;</button>' : ''}
            </div>
        `).join('');

        // Directory click → browse into
        listEl.querySelectorAll('.ftp-dir').forEach(el => {
            el.addEventListener('dblclick', () => {
                const path = (el as HTMLElement).dataset.path;
                if (path) this.browseRemote(path);
            });
        });

        // Download button
        listEl.querySelectorAll('.ftp-download-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const entry = (btn as HTMLElement).closest('.ftp-entry') as HTMLElement;
                const path = entry?.dataset.path;
                const name = entry?.querySelector('.ftp-entry-name')?.textContent;
                if (path && name) {
                    this.service.requestDownload(path, name).catch(err =>
                        console.error('[FileTransfer] Download failed:', err)
                    );
                }
            });
        });
    }

    private static readonly MAX_UPLOAD_SIZE = 10 * 1024 * 1024 * 1024; // 10 GB — matches Constants.MaxFileTransferSize

    private async uploadFiles(files: File[]): Promise<void> {
        for (const file of files) {
            // M8: Prevent browser OOM on huge files
            if (file.size > FileTransferPanel.MAX_UPLOAD_SIZE) {
                console.warn(`[FileTransfer] File too large: ${file.name} (${this.formatSize(file.size)} > ${this.formatSize(FileTransferPanel.MAX_UPLOAD_SIZE)})`);
                continue;
            }
            if (file.size === 0) {
                console.warn(`[FileTransfer] Skipping empty file: ${file.name}`);
                continue;
            }
            const remotePath = `${this.currentRemotePath}${this.currentRemotePath.endsWith('\\') ? '' : '\\'}${file.name}`;
            try {
                const transferId = await this.service.uploadFile(file, remotePath);
                this.addTransferRow(transferId, file.name, 'upload');
            } catch (error: any) {
                console.error(`[FileTransfer] Upload failed: ${file.name}`, error);
            }
        }
    }

    // WC-10: Transfer progress bars
    private addTransferRow(transferId: string, fileName: string, direction: 'upload' | 'download'): void {
        const container = this.panel?.querySelector('#ftp-transfers');
        if (!container) return;

        const row = document.createElement('div');
        row.className = 'ftp-transfer-row';
        row.id = `ftp-transfer-${transferId}`;
        row.innerHTML = `
            <div class="ftp-transfer-info">
                <span class="ftp-transfer-icon">${direction === 'upload' ? '&#x2B06;' : '&#x2B07;'}</span>
                <span class="ftp-transfer-name">${this.escapeHtml(fileName)}</span>
                <button class="ftp-cancel-btn" data-transfer-id="${transferId}" title="ביטול">&times;</button>
            </div>
            <div class="ftp-progress-bar">
                <div class="ftp-progress-fill" style="width: 0%"></div>
            </div>
            <div class="ftp-transfer-status">ממתין...</div>
        `;

        row.querySelector('.ftp-cancel-btn')?.addEventListener('click', () => {
            this.service.cancelTransfer(transferId).catch(() => {});
        });

        container.appendChild(row);
    }

    /** Update progress bar for a transfer (called from outside when progress events fire) */
    updateTransferProgress(progress: FileTransferProgress): void {
        const row = this.panel?.querySelector(`#ftp-transfer-${progress.transferId}`);
        if (!row) return;

        const fill = row.querySelector('.ftp-progress-fill') as HTMLElement;
        const status = row.querySelector('.ftp-transfer-status') as HTMLElement;

        if (fill) fill.style.width = `${progress.percent}%`;
        if (status) {
            switch (progress.status) {
                case 'transferring':
                    status.textContent = `${progress.percent}% — ${this.formatSize(progress.bytesTransferred)} / ${this.formatSize(progress.totalBytes)}`;
                    break;
                case 'completed':
                    status.textContent = 'הושלם ✓';
                    row.classList.add('ftp-transfer-done');
                    break;
                case 'failed':
                    status.textContent = `נכשל: ${progress.error || ''}`;
                    row.classList.add('ftp-transfer-error');
                    break;
                case 'cancelled':
                    status.textContent = 'בוטל';
                    row.classList.add('ftp-transfer-error');
                    break;
                default:
                    status.textContent = `${progress.percent}%`;
            }
        }
    }

    private formatSize(bytes: number): string {
        if (bytes === 0) return '0 B';
        const units = ['B', 'KB', 'MB', 'GB'];
        const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
        return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
    }

    private escapeHtml(str: string): string {
        return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    private injectStyles(): void {
        if (document.getElementById('ftp-panel-styles')) return;

        const style = document.createElement('style');
        style.id = 'ftp-panel-styles';
        style.textContent = `
            .ftp-panel {
                position: fixed; top: 0; right: -400px; width: 400px; height: 100vh;
                background: #0F1923; border-left: 1px solid #1C2938;
                z-index: 10000; display: flex; flex-direction: column;
                transition: right 0.3s ease; font-family: 'Segoe UI', sans-serif;
                box-shadow: -4px 0 20px rgba(0,0,0,0.5);
            }
            .ftp-panel.ftp-open { right: 0; }
            .ftp-header {
                display: flex; align-items: center; justify-content: space-between;
                padding: 12px 16px; border-bottom: 1px solid #1C2938;
                background: #141E2A;
            }
            .ftp-title { color: #E0E8F0; font-size: 15px; font-weight: 600; }
            .ftp-close-btn {
                width: 28px; height: 28px; border: none; background: transparent;
                color: #8899AA; font-size: 18px; cursor: pointer; border-radius: 6px;
                display: flex; align-items: center; justify-content: center;
            }
            .ftp-close-btn:hover { background: #1C2938; color: white; }
            .ftp-body { flex: 1; overflow-y: auto; display: flex; flex-direction: column; gap: 1px; }
            .ftp-remote-section { flex: 1; min-height: 200px; display: flex; flex-direction: column; }
            .ftp-path-bar {
                display: flex; gap: 4px; padding: 8px; background: #141E2A;
            }
            .ftp-path-input {
                flex: 1; background: #0A1118; border: 1px solid #1C2938; border-radius: 4px;
                color: #C0D0E0; padding: 4px 8px; font-size: 12px; font-family: Consolas, monospace;
            }
            .ftp-up-btn, .ftp-go-btn {
                width: 28px; height: 28px; border: 1px solid #1C2938; background: #141E2A;
                color: #8899AA; cursor: pointer; border-radius: 4px; font-size: 14px;
            }
            .ftp-up-btn:hover, .ftp-go-btn:hover { background: #1C2938; color: white; }
            .ftp-file-list {
                flex: 1; overflow-y: auto; padding: 4px;
            }
            .ftp-entry {
                display: flex; align-items: center; gap: 8px; padding: 6px 8px;
                border-radius: 4px; cursor: default; font-size: 13px; color: #C0D0E0;
            }
            .ftp-entry:hover { background: #1C2938; }
            .ftp-dir { cursor: pointer; }
            .ftp-entry-icon { font-size: 16px; flex-shrink: 0; }
            .ftp-entry-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
            .ftp-entry-size { color: #667788; font-size: 11px; flex-shrink: 0; }
            .ftp-download-btn {
                width: 24px; height: 24px; border: none; background: transparent;
                color: #4488CC; cursor: pointer; border-radius: 4px; font-size: 14px;
            }
            .ftp-download-btn:hover { background: #1C2938; }
            .ftp-drop-zone {
                padding: 16px; text-align: center; border: 2px dashed #1C2938;
                margin: 8px; border-radius: 8px; transition: all 0.2s;
            }
            .ftp-drop-zone.ftp-drop-active {
                border-color: #2563eb; background: rgba(37, 99, 235, 0.1);
            }
            .ftp-drop-text { color: #667788; font-size: 13px; margin-bottom: 8px; }
            .ftp-upload-btn {
                padding: 6px 16px; background: #2563eb; color: white; border: none;
                border-radius: 6px; cursor: pointer; font-size: 13px;
            }
            .ftp-upload-btn:hover { background: #1d4ed8; }
            .ftp-transfers { padding: 4px 8px; }
            .ftp-transfer-row {
                padding: 8px; margin-bottom: 4px; background: #141E2A;
                border-radius: 6px; border: 1px solid #1C2938;
            }
            .ftp-transfer-info {
                display: flex; align-items: center; gap: 6px; margin-bottom: 4px;
            }
            .ftp-transfer-name {
                flex: 1; color: #C0D0E0; font-size: 12px;
                overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
            }
            .ftp-cancel-btn {
                width: 20px; height: 20px; border: none; background: transparent;
                color: #FF6B6B; cursor: pointer; font-size: 14px;
            }
            .ftp-progress-bar {
                height: 4px; background: #1C2938; border-radius: 2px; overflow: hidden;
            }
            .ftp-progress-fill {
                height: 100%; background: #2563eb; border-radius: 2px;
                transition: width 0.3s ease;
            }
            .ftp-transfer-status {
                color: #667788; font-size: 11px; margin-top: 4px;
            }
            .ftp-transfer-done .ftp-progress-fill { background: #16a34a; }
            .ftp-transfer-done .ftp-transfer-status { color: #16a34a; }
            .ftp-transfer-error .ftp-progress-fill { background: #FF6B6B; }
            .ftp-transfer-error .ftp-transfer-status { color: #FF6B6B; }
            .ftp-loading, .ftp-empty, .ftp-error {
                padding: 20px; text-align: center; color: #667788; font-size: 13px;
            }
            .ftp-error { color: #FF6B6B; }
        `;
        document.head.appendChild(style);
    }
}
